// ─────────────────────────────────────────────────────
// Products Page
// ─────────────────────────────────────────────────────
import { useState } from 'react';
import type { Product, Alert, AgentState, Retailer } from '../api/client';
import { T, SectionHeader, Chip, Card, ScoreRing, RISK_COLOR } from '../components/ui';

interface PageProps {
 alerts: Alert[]; agents: AgentState[]; retailers: Retailer[];
 products: Product[];
 heartbeat: { agentsActive: number; openAlerts: number; uptime: number } | null;
 refetchAlerts: () => void; refetchAgents: () => void; refetchProducts: () => void;
 setAlerts: (fn: (prev: Alert[]) => Alert[]) => void;
}

export default function ProductsPage({ products, retailers }: PageProps) {
 const [search, setSearch] = useState('');
 const [retailerF, setRetailerF] = useState('all');
 const [riskF, setRiskF] = useState('all');
 const [selected, setSelected] = useState<Product | null>(null);

 const filtered = products.filter(p => {
 const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
 const matchR = retailerF === 'all' || p.retailer_slug === retailerF || p.retailer_id === retailerF;
 const matchRisk = riskF === 'all' || p.risk_level === riskF;
 return matchSearch && matchR && matchRisk;
 });

 return (
 <div className="fade-up">
 <SectionHeader title="Product Catalog" sub={`${products.length} products tracked across ${retailers.length} retailers`} />

 <div style={{ display: 'flex', gap: 10, marginBottom: 18 }}>
 <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products…"
 style={{ flex: 1, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 14px', color: T.text, fontSize: 12, outline: 'none', fontFamily: "'DM Sans', sans-serif" }} />
 <select value={retailerF} onChange={e => setRetailerF(e.target.value)}
 style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 12px', color: T.text, fontSize: 12, outline: 'none', cursor: 'pointer' }}>
 <option value="all">All Retailers</option>
 {retailers.map(r => <option key={r.id} value={r.slug}>{r.name}</option>)}
 </select>
 <select value={riskF} onChange={e => setRiskF(e.target.value)}
 style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 9, padding: '9px 12px', color: T.text, fontSize: 12, outline: 'none', cursor: 'pointer' }}>
 <option value="all">All Risk</option>
 {['LOW','MEDIUM','HIGH','CRITICAL'].map(r => <option key={r} value={r}>{r}</option>)}
 </select>
 </div>

 <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 380px' : '1fr', gap: 18 }}>
 <div>
 <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
 {filtered.length === 0 ? <div style={{ color: T.sub, fontSize: 12 }}>No products match filters</div> : null}
 {filtered.map(p => (
 <div key={p.id} onClick={() => setSelected(selected?.id === p.id ? null : p)} style={{
 background: selected?.id === p.id ? T.lift : T.panel,
 border: `1px solid ${selected?.id === p.id ? T.copper + '50' : T.border}`,
 borderRadius: 12, padding: '15px', cursor: 'pointer', transition: 'all .12s',
 }}>
 <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
 <div style={{ fontSize: 28 }}>{p.img_emoji}</div>
 <div style={{ flex: 1, minWidth: 0 }}>
 <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
 <div style={{ fontSize: 10, color: T.sub, marginBottom: 8 }}>{p.brand} · {p.origin_description}</div>
 <div style={{ display: 'flex', gap: 6 }}>
 <Chip label={p.risk_level} color={RISK_COLOR[p.risk_level] || T.sub} />
 {p.verified && <Chip label="Verified" color={T.sage} />}
 {p.blockchain_hash && <Chip label="On-chain" color={T.sapphire} />}
 </div>
 </div>
 <ScoreRing score={p.provenance_score} size={48} />
 </div>
 </div>
 ))}
 </div>
 </div>

 {selected && (
 <Card style={{ position: 'sticky', top: 60, alignSelf: 'flex-start' }}>
 <div style={{ fontSize: 36, marginBottom: 12 }}>{selected.img_emoji}</div>
 <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: 18, color: T.text, marginBottom: 4 }}>{selected.name}</div>
 <div style={{ color: T.sub, fontSize: 12, marginBottom: 16 }}>{selected.brand}</div>
 <ScoreRing score={selected.provenance_score} size={72} />
 <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
 {[
 ['Origin', selected.origin_description],
 ['Category', selected.category],
 ['Price', `$${selected.price_usd}`],
 ['Risk', selected.risk_level],
 ['Verified', selected.verified ? 'Yes' : 'No'],
 ['Flags', String(selected.flag_count)],
 ['Blockchain', selected.blockchain_hash ? selected.blockchain_hash.slice(0, 18) + '…' : 'Not anchored'],
 ].map(([k, v]) => (
 <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, borderBottom: `1px solid ${T.border}`, paddingBottom: 6 }}>
 <span style={{ color: T.sub }}>{k}</span>
 <span style={{ color: T.text, fontFamily: k === 'Blockchain' ? "'DM Mono', monospace" : 'inherit', fontSize: k === 'Blockchain' ? 10 : 11 }}>{v}</span>
 </div>
 ))}
 </div>
 </Card>
 )}
 </div>
 </div>
 );
}

